import { pointFrom } from "@excalidraw/math";

import type { LocalPoint } from "@excalidraw/math";

import { segmentToCurve } from "./pathGeometry";

import type { PenPointHandles } from "./types";

export type PenPathData = Readonly<{
  points: readonly LocalPoint[];
  handles: readonly PenPointHandles[];
  polygon: boolean;
}>;

export type PenPathUpdate = {
  points: LocalPoint[];
  handles: PenPointHandles[];
  polygon?: boolean;
};

const clone = (data: PenPathData): PenPathUpdate => ({
  points: data.points.map((pt) => pointFrom<LocalPoint>(pt[0], pt[1])),
  handles: data.handles.map((h) =>
    h
      ? {
          ...(h.in ? { in: pointFrom<LocalPoint>(h.in[0], h.in[1]) } : {}),
          ...(h.out ? { out: pointFrom<LocalPoint>(h.out[0], h.out[1]) } : {}),
        }
      : null,
  ),
});

/** closed paths duplicate anchor 0 at n-1 — treat them as one logical node */
const withMirror = (data: PenPathData, indices: number[]): number[] => {
  if (!data.polygon) {
    return indices;
  }
  const last = data.points.length - 1;
  const set = new Set(indices);
  if (set.has(0)) {
    set.add(last);
  }
  if (set.has(last)) {
    set.add(0);
  }
  return [...set];
};

export const makeCorner = (
  data: PenPathData,
  indices: number[],
): PenPathUpdate => {
  const next = clone(data);
  for (const i of withMirror(data, indices)) {
    next.handles[i] = null;
  }
  return next;
};

export const makeCurve = (
  data: PenPathData,
  indices: number[],
): PenPathUpdate => {
  const next = clone(data);
  const { points } = data;
  // closed paths duplicate points[0] at points[n-1] (the seam); the distinct
  // ring is points[0..n-2], so neighbors must wrap modulo that ring length
  // to give both seam copies (index 0 and n-1) identical tangents.
  const ring = points.length - 1;
  for (const i of withMirror(data, indices)) {
    let prev: LocalPoint;
    let after: LocalPoint;
    if (data.polygon) {
      const idx = i % ring;
      prev = points[(idx - 1 + ring) % ring];
      after = points[(idx + 1) % ring];
    } else {
      prev = points[i - 1] ?? points[i];
      after = points[i + 1] ?? points[i];
    }
    let dx = after[0] - prev[0];
    let dy = after[1] - prev[1];
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const dPrev = Math.hypot(points[i][0] - prev[0], points[i][1] - prev[1]);
    const dNext = Math.hypot(after[0] - points[i][0], after[1] - points[i][1]);
    const mag = (dPrev + dNext) / 8;
    next.handles[i] = {
      in: pointFrom<LocalPoint>(-dx * mag, -dy * mag),
      out: pointFrom<LocalPoint>(dx * mag, dy * mag),
    };
  }
  return next;
};

export const addNodeOnSegment = (
  data: PenPathData,
  segmentIndex: number,
  t: number = 0.5,
): PenPathUpdate => {
  const next = clone(data);
  const i = segmentIndex;
  const isCurve = !!(data.handles[i]?.out || data.handles[i + 1]?.in);
  const lerp = (a: LocalPoint, b: LocalPoint): LocalPoint =>
    pointFrom<LocalPoint>(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t);
  if (!isCurve) {
    next.points.splice(i + 1, 0, lerp(data.points[i], data.points[i + 1]));
    next.handles.splice(i + 1, 0, null);
    return next;
  }
  // de Casteljau split at t
  const [p0, c1, c2, p1] = segmentToCurve(data.points, data.handles, i);
  const q0 = lerp(p0, c1);
  const q1 = lerp(c1, c2);
  const q2 = lerp(c2, p1);
  const r0 = lerp(q0, q1);
  const r1 = lerp(q1, q2);
  const s = lerp(r0, r1); // the new anchor, on the curve
  next.points.splice(i + 1, 0, s);
  next.handles[i] = {
    ...(next.handles[i] ?? {}),
    out: pointFrom<LocalPoint>(q0[0] - p0[0], q0[1] - p0[1]),
  };
  next.handles.splice(i + 1, 0, {
    in: pointFrom<LocalPoint>(r0[0] - s[0], r0[1] - s[1]),
    out: pointFrom<LocalPoint>(r1[0] - s[0], r1[1] - s[1]),
  });
  next.handles[i + 2] = {
    ...(next.handles[i + 2] ?? {}),
    in: pointFrom<LocalPoint>(q2[0] - p1[0], q2[1] - p1[1]),
  };
  return next;
};

export const deleteNodes = (
  data: PenPathData,
  indices: number[],
): PenPathUpdate | null => {
  const remove = new Set(withMirror(data, indices));
  const points = data.points.filter((_, i) => !remove.has(i));
  if (points.length < 2) {
    return null;
  }
  const next = clone(data);
  next.points = points.map((pt) => pointFrom<LocalPoint>(pt[0], pt[1]));
  next.handles = next.handles.filter(
    (_, i) => !remove.has(i),
  ) as PenPointHandles[];
  if (data.polygon && (remove.has(0) || remove.has(data.points.length - 1))) {
    next.polygon = false;
  }
  return next;
};

export const mergeNodes = (
  data: PenPathData,
  indices: number[],
): PenPathUpdate | null => {
  const sorted = [...indices].sort((a, b) => a - b);
  if (sorted.length < 2) {
    return null;
  }
  for (let k = 1; k < sorted.length; k++) {
    if (sorted[k] !== sorted[k - 1] + 1) {
      return null; // must be contiguous
    }
  }
  if (data.polygon) {
    const last = data.points.length - 1;
    // ponytail: only merges anchored at the opening seam copy (run starts at
    // index 0, doesn't reach the closing copy n-1) are handled across the seam.
    // A wrap-around run — one that touches the closing copy n-1 without starting
    // at 0 (e.g. [n-2, n-1], which is logically nodes {n-2, 0}) — is disallowed
    // (null) to avoid emitting a malformed path. Upgrade path: rotate the
    // distinct ring so the wrap-around run is contiguous, merge, then re-close.
    if (sorted[sorted.length - 1] === last && sorted[0] !== 0) {
      return null;
    }
  }
  const cx =
    sorted.reduce((sum, i) => sum + data.points[i][0], 0) / sorted.length;
  const cy =
    sorted.reduce((sum, i) => sum + data.points[i][1], 0) / sorted.length;
  const next = clone(data);
  next.points.splice(sorted[0], sorted.length, pointFrom<LocalPoint>(cx, cy));
  next.handles.splice(sorted[0], sorted.length, null);
  if (next.points.length < 2) {
    return null;
  }
  if (data.polygon) {
    next.polygon = true;
    // collapsing a run that includes the opening seam copy (index 0) leaves the
    // closing copy (now at the new last index) stale — re-close it onto the
    // merged centroid so points[0] === points[last] and both seam copies stay
    // corners (a merge yields a corner). Interior merges never touch the seam.
    if (sorted[0] === 0) {
      const seam = next.points.length - 1;
      next.points[seam] = pointFrom<LocalPoint>(cx, cy);
      next.handles[seam] = null;
    }
  }
  return next;
};

export const joinEndpoints = (data: PenPathData): PenPathUpdate | null => {
  if (data.polygon || data.points.length < 3) {
    return null;
  }
  const next = clone(data);
  next.points.push(pointFrom<LocalPoint>(data.points[0][0], data.points[0][1]));
  next.handles.push(null);
  next.polygon = true;
  return next;
};

export const separateAtNodes = (
  data: PenPathData,
  indices: number[],
): { first: PenPathUpdate; second: PenPathUpdate | null } | null => {
  if (indices.length !== 1) {
    return null;
  }
  let i = indices[0];
  if (data.polygon) {
    // index n-1 is the same logical node as index 0 (the seam duplicate) —
    // normalize it before using it to index the deduplicated ring below.
    const last = data.points.length - 1;
    if (i === last) {
      i = 0;
    }
    if (i < 0 || i >= data.points.length) {
      return null;
    }
    // closed → open at node i: rotate so i is the start, drop the duplicate
    const pts = [...data.points.slice(0, -1)];
    const hs = [...data.handles.slice(0, -1)];
    const rotatedPts = [...pts.slice(i), ...pts.slice(0, i), pts[i]];
    const rotatedHs = [...hs.slice(i), ...hs.slice(0, i), hs[i]];
    return {
      first: {
        points: rotatedPts.map((pt) => pointFrom<LocalPoint>(pt[0], pt[1])),
        handles: rotatedHs as PenPointHandles[],
        polygon: false,
      },
      second: null,
    };
  }
  if (i <= 0 || i >= data.points.length - 1) {
    return null; // endpoints of an open path can't be split
  }
  const src = clone(data);
  return {
    first: {
      points: src.points.slice(0, i + 1),
      handles: src.handles.slice(0, i + 1),
      polygon: false,
    },
    second: {
      points: src.points.slice(i),
      handles: src.handles.slice(i),
      polygon: false,
    },
  };
};

export const nudgeNodes = (
  data: PenPathData,
  indices: number[],
  dx: number,
  dy: number,
): PenPathUpdate => {
  const next = clone(data);
  for (const i of withMirror(data, indices)) {
    next.points[i] = pointFrom<LocalPoint>(
      next.points[i][0] + dx,
      next.points[i][1] + dy,
    );
  }
  return next;
};

export type HandlesEdit =
  | { type: "delete"; indices: number[] }
  | { type: "append"; count: number }
  | { type: "insert"; index: number };

/** keeps the handles array parallel to points when shared editor code mutates points */
export const syncHandlesAfterPointEdit = (
  handles: readonly PenPointHandles[],
  edit: HandlesEdit,
): PenPointHandles[] => {
  switch (edit.type) {
    case "delete": {
      const remove = new Set(edit.indices);
      return handles.filter((_, i) => !remove.has(i));
    }
    case "append":
      return [...handles, ...Array(edit.count).fill(null)];
    case "insert": {
      const next = [...handles];
      next.splice(edit.index, 0, null);
      return next;
    }
  }
};
